import CustomDataTable, { Column } from "../Common/CustomDataTable";
import "../../styles/clientPage.css";
import { useEffect, useMemo, useState } from "react";
import SlideOver from "../Common/SlideOver";
import ClientInteractionActionView from "./ClientInteractionActionView";
import { capitalizeFirstLetter, formatCurrency, getPresetDateRange, getTrendIcon } from "../../utils/utils";
import { useLocation } from "react-router-dom";
import { format, subDays } from "date-fns";
import QuickTopicsPopper from "../Common/QuickTopicsPopper";
interface ClientExecAttentionProps {
  ratedClientsData: any;
  showSpinner?: boolean;
  parentPageIdentifier?: string;
}

const ClientExecAttention = ({ ratedClientsData, showSpinner, parentPageIdentifier }: ClientExecAttentionProps) => {
  const location = useLocation();
  const [selectedClientDomain, setSelectedClientDomain] = useState<string | null>( location.state?.clientDomain || null);
  const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);
  const [clientOrgId, setClientOrgId] = useState<string>(location.state?.orgId);
  const [subOrgId, setSubOrgId] = useState<string>(location.state?.subOrgId);
  const slideOverParent = "exec-attention";
  const incomingSlideOverParent = location.state?.slideOverParent;
  const [selectedClientName, setSelectedClientName] = useState<string>(location?.state?.clientName);

  const lastThreeMonths = useMemo(() => getPresetDateRange("last3MonthsFull"), []);
  const defaultDateRange = {
    startDate: lastThreeMonths.start_time,
    endDate: lastThreeMonths.end_time,
  };
  const initialDateRange =
    location.state?.customDateRange ??
    defaultDateRange;
  const [customDateRange, setCustomDateRange] = useState<{ startDate: string; endDate: string } | undefined>(
    initialDateRange
  );

  useEffect(() => {
      if (incomingSlideOverParent === slideOverParent) {
        setIsSlideOverOpen(true);      
      }
      setCustomDateRange(defaultDateRange);
    }, [incomingSlideOverParent]);

  const handleClientClick = (email: string, name: string, clientOrgId: string, subOrgId: string) => {
    const domain = email.split("@")[1] || email;
    setSelectedClientDomain(domain);
    setSelectedClientName(name);
    setIsSlideOverOpen(true);
    setClientOrgId(clientOrgId);
    setSubOrgId(subOrgId);
    setCustomDateRange(defaultDateRange);
  };

  // Sentiment → color
  const sentimentToColor = (sentiment: string) => {
    switch (sentiment) {
      case "Positive": return "#1EC96B";
      case "Negative": return "#FF4D4D";
      case "Neutral":  return "#F7B750";
      default: return "#BDBDBD";         // gray fallback
    }
  };  
  const scoreToColor = (score: number) => {
    switch (score) {
      case 1: return "#FF4D4D";
      case 5: return "#F7B750";
      case 9: return "#1EC96B";
      default: return "#BDBDBD";
    }
  };

  const formatMonth = (monthStr: string) => {
    const [y, m] = monthStr.split("-").map((part) => Number(part));
    if (!y || !m || m < 1 || m > 12) return monthStr;
    return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "short" });
  };

  const normalizeMonthKey = (key: string) => {
    const match = /^(\d{4})-(\d{1,2})$/.exec(key.trim());
    if (!match) return key;
    return `${match[1]}-${match[2].padStart(2, "0")}`;
  };

  const normalizeMonthlyTrend = (trend: Record<string, string> | undefined | null) => {
    if (!trend || typeof trend !== "object" || Array.isArray(trend)) return {};
    const out: Record<string, string> = {};
    for (const k of Object.keys(trend)) {
      out[normalizeMonthKey(k)] = trend[k];
    }
    return out;
  };

  const getLastNMonths = (n: number): string[] => {
    const months: string[] = [];
    const now = new Date();
    const endOfMonth = new Date(
      now.getFullYear(),
      now.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );

    // Check if current month is complete
    const isMonthComplete = now.getTime() > endOfMonth.getTime();

    // Decide starting point
    const startMonthOffset = isMonthComplete ? 0 : 1;

    for (let i = n - 1 + startMonthOffset; i >= startMonthOffset; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      months.push(key);
    }

    return months;
  };
  const last3Months = getLastNMonths(3);

  // 🔹 Map from ratedClientsData.clientsWithLastTwoNegativeInteractions
  const mappedClients = useMemo(() => {
    const clients =
      ratedClientsData?.clientsWithLastTwoNegativeInteractions || [];
  
    return clients
      .map((client: any) => ({
        name: client.clientName,
        email: client.primaryEmail || client.clientDomain,
        arr: client.arr ?? 0,
        mrr: client.mrr ?? 0,
        clientOrgId: client.clientOrgId,
        subOrgId: client.subOrgId,
        monthlyTrend: client.monthlyTrend,
        score: Number(client.score),
        ratings: [...(client.lastThreeRatedInteractions || [])]
          .sort(
            (a: any, b: any) =>
              new Date(a.start_time).getTime() -
              new Date(b.start_time).getTime()
          )
          .map((interaction: any) => ({
            interaction_id: interaction.interaction_id,
            color: sentimentToColor(interaction.overall_sentiment),
            summary: interaction.summary,
            startTime: interaction.start_time,
          })),
      }))
      .sort((a: any, b: any) => b.arr - a.arr);
  }, [ratedClientsData]);  
    
  const columns: Column[] = [
    {
      key: "index",
      header: "#",
      render: (_, __, index) => index + 1,
      align: "center",
      width: "5%",
    },
    {
      key: "name",
      header: "Client Name",
      align: "left",
      width: "20%",
      render: (_, row) => (
        <div
          className="client-name-box"
          style={{ cursor: "pointer", display: "flex", flexDirection: "row" }}
          onClick={() => handleClientClick(row.email, row.name, row.clientOrgId, row.subOrgId) }
        >
          <div className="client-name">{capitalizeFirstLetter(row.name)}</div>
          <QuickTopicsPopper
            orgId={row.clientOrgId}
            subOrgId={row.subOrgId}
          />
        </div>
      ),
    },
    {
      key: "monthlyTrend",
      header: "Monthly Trend",
      align: "center",
      width: "25%",
      render: (monthlyTrend) => {
        if (!monthlyTrend) return "-";
        const trend = normalizeMonthlyTrend(monthlyTrend as Record<string, string>);
        const monthKeys = last3Months.map(normalizeMonthKey);
        const monthsToRender = monthKeys.map((month) => ({
          month,
          value: trend[month],
          hasTrend:
            Object.prototype.hasOwnProperty.call(trend, month) &&
            trend[month] != null &&
            trend[month] !== "",
        }));
        return (
          <div className="exec-monthly-trend">
            <div className="exec-monthly-trend__row">
              {monthsToRender.map(({ month, value, hasTrend }) => (
                <div key={month} className="exec-monthly-trend__slot">
                  {hasTrend ? (
                    <>
                      <img
                        className="exec-monthly-trend__icon"
                        src={getTrendIcon(value)}
                        alt="trend"
                      />
                      <span className="exec-monthly-trend__label">
                        {formatMonth(month)}
                      </span>
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        );
      },
    },
    {
      key: "score",
      header: "Current Rating",
      align: "center",
      width: "20%",
      render: (_, row) => {
        if (!row.score) return "-";

        const color = scoreToColor(row.score);
        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: color,
              }}
            />
          </div>
        );
      },
    },
    {
      key: "mrr",
      header: "MRR ($)",
      align: "center",
      width: "15%",
      render: (value) => formatCurrency(Number(value)),
    },
  ];
  const yesterday = format(subDays(new Date(), 1), "MMM d, yyyy");

  return (
    <div className="section">
      <div className="section__header">
        <h2 className="section__title">Attention Needed
        <span className="section__title_sub">(As on {yesterday})</span>
        </h2>
      </div>

      <CustomDataTable
        data={mappedClients}
        columns={columns}
        pageSize={5}
        className="exec-table"
        showSpinner= {showSpinner}
      />

      <SlideOver isOpen={isSlideOverOpen} onClose={() => setIsSlideOverOpen(false)}>
        {selectedClientDomain && (
          <ClientInteractionActionView
            clientDomain={selectedClientDomain}
            isSlideOverOpen={isSlideOverOpen}
            slideOverParent={slideOverParent}
            parentPageIdentifier={parentPageIdentifier}
            relationshipType="client"
            customDateRange={customDateRange}
            clientOrgId={clientOrgId}
            subOrgId={subOrgId}
            clientName={selectedClientName}
          />
        )}
      </SlideOver>
    </div>
  );
};

export default ClientExecAttention;
